import clsx from 'clsx';
import { useMemo } from 'react';
import useDOMPurify from '../../common/hooks/use-dom-purify';
import useTestTag from '../../common/hooks/use-test-tag';
import { parseDate } from '../../common/functions/parse-date';
import RichTextArea from '../fields/rich-text-area/rich-text-area.component';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

const USER_TIME_ZONE = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;
const DATE_FORMATTER = Intl.DateTimeFormat(navigator.language, { dateStyle: 'short', timeZone: USER_TIME_ZONE });
const TIME_FORMATTER = Intl.DateTimeFormat(navigator.language, { timeStyle: 'short', timeZone: USER_TIME_ZONE });

export interface CommentElementProps {
  id?: string | number;
  draft?: boolean;
  imageSrc?: string;
  user: string;
  date: string;
  message: string;
  readOnly?: boolean;
}

const _formatDisplayDate = (date: Date | null) => {
  if (!date) {
    return '-';
  }
  return DATE_FORMATTER.format(date);
};

const _formatDisplayTime = (date: Date | null) => {
  if (!date) {
    return '-';
  }
  return TIME_FORMATTER.format(date);
};

export default function CommentElement(props: CommentElementProps): React.JSX.Element {
  const { id, user, date, message, readOnly } = props;
  const _date = useMemo(() => parseDate(date), [date]);

  const { sanitize } = useDOMPurify();
  const value = useMemo(() => sanitize(message ?? ''), [message, sanitize]);

  const container = {
    wrapper: 'odin-pt-2 odin-mt-2 odin-flex odin-border-t odin-border-gray-4',
    image: 'odin-rounded-full',
    avatar: 'odin-mt-px',
    details: 'odin-flex odin-flex-col odin-w-full',
    user: clsx('odin-font-medium odin-text-sm', {
      'odin-text-readonly': readOnly,
    }),
    dateWrapper: 'odin-flex odin-text-xs odin-my-2',
    timeIcon: 'odin-ml-4',
    date: 'odin-mr-1 odin-ml-1.5',
    message: 'odin-mt-3 odin-mb-5 odin-text-sm odin-text-justify odin-font-normal',
  };

  const tagCreator = useTestTag('comment-element');

  return (
    <div key={id} className={container.wrapper} ref={tagCreator()}>
      <div className={container.details}>
        <h3 className={container.user}>{user}</h3>
        <div className={container.dateWrapper}>
          <OdinIcon icon={'CalendarCheck'} size={OdinIconSize.ExtraSmall} />
          <p className={container.date}>{_formatDisplayDate(_date)}</p>
          <OdinIcon icon={'Clock'} size={OdinIconSize.ExtraSmall} className={container.timeIcon} />
          <p className={container.date}>{_formatDisplayTime(_date)}</p>
        </div>
        <RichTextArea value={value} isEditor={false} readOnly={true} />
      </div>
    </div>
  );
}
